import { gql } from "@apollo/client";
import { INTEGRATION_WITH_CHECK_CREATE_TEMPLATES_CONFIG_FRAGMENT } from "@/modules/integrations/queries.js";

export const BASIC_CHECK_FRAGMENT = gql`
  fragment BasicCheckFragment on Check {
    id
    gid: id
    name
    notes: rawNotes
    report_href: reportHref
    reportHref
    url
    owner {
      ... on Team {
        name
        href
        id
        gid: id
      }
      ... on User {
        name
        href
        id
        gid: id
      }
    }
    type
    args
    enabled
    enableOn
    description
    category {
      name
      id
      gid: id
    }
    checkLevels {
      level {
        name
        index
        id
        gid: id
      }
    }
    campaign {
      id
      name
      status
      href
    }
    filter {
      name
      id
      gid: id
      path
      href
    }
    integration {
      ...IntegrationWithCheckCreateTemplateConfigFragment
    }
    container {
      permissions {
        canUpdate
      }
    }
  }
  ${INTEGRATION_WITH_CHECK_CREATE_TEMPLATES_CONFIG_FRAGMENT}
`;

export const EVALUATE_CHECK_TEMPLATE_QUERY = gql`
  query evaluateCheckTemplate(
    $expression: String!
    $template: String!
    $data: JSON!
    $serviceSelector: String
    $usesCtxAndParamsVariables: Boolean
    $sampleQueryParams: String
  ) {
    account {
      evaluateCheckTemplate(
        expression: $expression
        template: $template
        data: $data
        serviceSelector: $serviceSelector
        usesCtxAndParamsVariables: $usesCtxAndParamsVariables
        sampleQueryParams: $sampleQueryParams
      ) {
        checkStatus
        message
        errors {
          message
          path
          title
        }
        foundServices
      }
    }
  }
`;

export const CHECK_STATS_FRAGMENT = gql`
  fragment CheckStatsFragment on Check {
    stats {
      total
      totalSuccessful
    }
  }
`;

export const CHECK_CREATE_FRAGMENT = gql`
  fragment CheckCreateFragment on CheckCreatePayload {
    check {
      filter {
        id
      }
      ...BasicCheckFragment
      ...CheckStatsFragment
    }
    errors {
      message
      path
      title
    }
  }
  ${BASIC_CHECK_FRAGMENT}
  ${CHECK_STATS_FRAGMENT}
`;

const MUTATION_ALIAS_PREFIX = "op";

export const isMultiMutationAliasField = (fieldName) =>
  fieldName.startsWith(MUTATION_ALIAS_PREFIX);

export const buildMultiCreate = (inputs) => {
  if (!inputs || inputs.length === 0) {
    throw new TypeError("Inputs must be a non-empty array");
  }

  const varDefs = inputs
    .map((_, i) => `$input${i}: CheckCreateInput!`)
    .join(", ");

  const fields = inputs
    .map(
      (_, i) => `
      ${MUTATION_ALIAS_PREFIX}${i}: checkCreate(input: $input${i}) {
        ...CheckCreateFragment
      }
    `,
    )
    .join("\n");

  const mutation = gql`
    mutation(${varDefs}) {
      ${fields}
    }
    ${CHECK_CREATE_FRAGMENT}
  `;

  const variables = Object.fromEntries(
    inputs.map((input, i) => [`input${i}`, input]),
  );

  return { mutation, variables };
};

export const CREATE_CHECK_MUTATION = gql`
  mutation checkCreation(
    $name: String!
    $type: CheckType!
    $notes: String
    $ownerId: ID
    $args: JSON
    $filterId: ID
    $levelId: ID
    $categoryId: ID
    $campaignId: ID
    $integrationId: ID
    $enabled: Boolean!
    $enableOn: ISO8601DateTime
  ) {
    checkCreate(
      input: {
        name: $name
        type: $type
        notes: $notes
        ownerId: $ownerId
        args: $args
        filterId: $filterId
        levelId: $levelId
        categoryId: $categoryId
        campaignId: $campaignId
        integrationId: $integrationId
        enabled: $enabled
        enableOn: $enableOn
      }
    ) {
      ...CheckCreateFragment
    }
  }
  ${CHECK_CREATE_FRAGMENT}
`;

export const DELETE_CHECK_MUTATION = gql`
  mutation checkDeletion($id: ID!) {
    checkDelete(input: { id: $id }) {
      deletedCheckId
    }
  }
`;

export const UPDATE_CHECK_MUTATION = gql`
  mutation checkUpdate(
    $id: ID!
    $ownerId: ID
    $name: String
    $notes: String
    $args: JSON
    $filterId: ID
    $enabled: Boolean
    $enableOn: ISO8601DateTime
    $levelId: ID
    $categoryId: ID
    $campaignId: ID
    $integrationId: ID
  ) {
    checkUpdate(
      input: {
        id: $id
        ownerId: $ownerId
        name: $name
        notes: $notes
        args: $args
        filterId: $filterId
        enabled: $enabled
        enableOn: $enableOn
        levelId: $levelId
        categoryId: $categoryId
        campaignId: $campaignId
        integrationId: $integrationId
      }
    ) {
      check {
        filter {
          id
        }
        ...BasicCheckFragment
        ...CheckStatsFragment
      }
      errors {
        message
        path
        title
      }
    }
  }
  ${BASIC_CHECK_FRAGMENT}
  ${CHECK_STATS_FRAGMENT}
`;

export const PARSE_CHECK_YAML_MUTATION = gql`
  mutation checkYamlParse($checkYaml: String!) {
    checkYamlParse(input: { checkYaml: $checkYaml }) {
      name
      type
      args
      notes
      errors {
        message
        path
      }
    }
  }
`;

export const CREATE_CHECK_RESULT_MUTATION = gql`
  mutation createCheckResult(
    $checkId: ID!
    $message: String
    $status: CheckResultStatusEnum!
    $serviceIdentifier: IdentifierInput!
  ) {
    checkResultCreate(
      input: {
        checkId: $checkId
        status: $status
        message: $message
        service: $serviceIdentifier
      }
    ) {
      checkResult {
        check {
          id
        }
        status
        message
        isInitialManualCheckResult
        service {
          id
        }
      }
      errors {
        message
        path
      }
    }
  }
`;

export const COPY_CHECKS_MUTATION = gql`
  mutation (
    $checkIds: [ID!]!
    $targetCategoryId: ID!
    $targetLevelId: ID
    $move: Boolean
  ) {
    checkCopy(
      input: {
        checkIds: $checkIds
        targetCategoryId: $targetCategoryId
        targetLevelId: $targetLevelId
        move: $move
      }
    ) {
      errors {
        message
        path
      }
      targetCategory {
        container {
          ... on Scorecard {
            id
            href
          }
          ... on Rubric {
            id
            href
          }
        }
      }
    }
  }
`;
